import type { ISubaccount, MarketDataCardUnifiedLiquidityMarket, Security } from '@talos/kyoko';
import {
  Button,
  EMPTY_ARRAY,
  FormControlSizes,
  FormGroup,
  Grid,
  isMultileg,
  MixpanelEvent,
  MixpanelEventProperty,
  OrderPresetIcon,
  Stack,
  SymbolSelector,
  SymbolSelectorAppearance,
  Toggle,
  useMarketAccountsContext,
  useMarketsContext,
  useMixpanel,
  useSecuritiesContext,
  useSecurity,
  useUnifiedLiquidity,
  useUnifiedLiquidityContext,
  useUnifiedLiquidityParameter,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag';
import { useSubAccounts } from '../../../../../providers';
import { useOrderPresets, type IOrderPreset } from '../../../../../providers/OrderPresetsContext';
import { useTradingSettings } from '../../../../../providers/TradingSettingsContext';
import type { SingleSymbolPanelConfig } from '../../../../../types/LayoutConfig';
import { usePanelContext } from '../../../../Layout/providers/PanelContextProvider';
import {
  getMultilegUnifiedLiquidityMarketAccounts,
  toMultilegSelectedMarketAccounts,
} from '../../../../MarketDataCard/multilegLegMarketAccounts';
import { useMarketSelectorBuyingPowerEnrichment } from '../../../../MarketSelector/enrichments/useMarketSelectorBuyingPowerEnrichment';
import { useUnsettledEnrichment } from '../../../../MarketSelector/enrichments/useUnsettledEnrichment';
import { useUnifiedLiquidityMarketSelectorExtensionFactory } from '../../../../MarketSelector/extensions/useUnifiedLiquidityMarketSelectorExtension';
import { useMarketDataMarketSelectorItems } from '../../../../MarketSelector/MarketData/useMarketDataMarketSelectorItems';
import { MarketSelector } from '../../../../MarketSelector/MarketSelector';
import { useMarketSelectorAggregationPresets } from '../../../../MarketSelector/presets/useMarketSelectorAggregationPresets';
import { getSelectedUnifiedLiquidityMarketTooltipGroups } from '../../../../MarketSelector/selectedUnifiedLiquidityMarketTooltip';
import type { MarketSelectorValue } from '../../../../MarketSelector/types';
import { UnifiedLiquidityMarketTooltipContent } from '../../../../MarketSelector/UnifiedLiquidityMarketTooltipContent';
import { useMarketSelector } from '../../../../MarketSelector/useMarketSelector';
import { getUnifiedLiquiditySelectionsForMarketAccounts } from '../../../../MarketSelector/utils/unifiedLiquidity';
import { useCreateMultilegDialog } from '../../../../MultilegCombo/CreateMultilegDialog';
import { MultiLegSummary } from '../../../../MultiLegSummary';
import {
  OrderPresetSearchSelect,
  useOrderPresetSearchSelectOptions,
} from '../../../../OrderPresets/OrderPresetSearchSelect';
import { SubAccountSearchSelect } from '../../../../SubAccounts/SubAccountSearchSelect';
import { useSubAccountSearchSelectOptions } from '../../../../SubAccounts/useSubAccountSearchSelectOptions';
import { getSelectableMarketItemNames } from './getSelectableMarketItemNames';

interface SingleSymbolPanelSettingsProps {
  availableSecurities: Security[];
  symbol: string | undefined;
  aggregation: string | undefined;
  markets: string[] | undefined;
  marketAccounts: string[] | undefined;
  onSymbolChange: (symbol: string | undefined) => void;
  onUpdateParameters: (config: Partial<SingleSymbolPanelConfig>) => void;
  onClose: () => void;
  allowMultileg: boolean;
  orderPresetID: string | undefined;
  subAccount?: string;
  enableUnifiedLiquidity: boolean | undefined;
  unifiedLiquidityMarketSymbols?: MarketDataCardUnifiedLiquidityMarket[];
  allowUnifiedLiquidity?: boolean;

  customSettings?: React.ReactNode;
  showPresets?: boolean;
  shoeMarketAccounts?: boolean;
}

export function SingleSymbolPanelSettings({
  symbol,
  markets,
  marketAccounts,
  onSymbolChange,
  onUpdateParameters,
  aggregation,
  availableSecurities,
  enableUnifiedLiquidity,
  unifiedLiquidityMarketSymbols,
  allowUnifiedLiquidity,
  allowMultileg,
  orderPresetID,
  subAccount,
  customSettings,
  onClose,
  showPresets = true,
  shoeMarketAccounts = true,
}: SingleSymbolPanelSettingsProps) {
  const mixpanel = useMixpanel();
  const { isPopout } = usePanelContext();

  const security = useSecurity(symbol);
  const { securitiesBySymbol } = useSecuritiesContext();
  const { marketsByName, marketDisplayNameByName } = useMarketsContext();
  const { marketAccountsByMarket, marketAccountsByName } = useMarketAccountsContext();
  const { unifiedLiquidityTokenBySymbol } = useUnifiedLiquidityContext();
  const { enableDefaultMarketCardSubAccount } = useTradingSettings();
  const { subAccountsByName } = useSubAccounts();
  const { updateOrderPreset, orderPresetsByID, orderPresetsList } = useOrderPresets();

  const subAccountOptions = useSubAccountSearchSelectOptions();
  const orderPreset = orderPresetID ? orderPresetsByID.get(orderPresetID) : undefined;
  const subAccountSelection = subAccount ? subAccountsByName.get(subAccount) : undefined;
  const unifiedLiquidityParameter = useUnifiedLiquidityParameter();
  const unifiedLiquidityToken = useUnifiedLiquidity(symbol);
  const showUnifiedLiquidityOption = !!unifiedLiquidityToken;
  const { enableSEDOLDisplay, unifiedLiquidityCombinePerpsAndCFDs } = useFeatureFlag();
  const orderPresetOptions = useOrderPresetSearchSelectOptions({ security });

  const filteredSecurities = useMemo(() => {
    return availableSecurities.filter(security => allowMultileg || !isMultileg(security));
  }, [availableSecurities, allowMultileg]);

  const selectedMarkets = markets ?? EMPTY_ARRAY;
  const selectedMarketAccounts = marketAccounts ?? EMPTY_ARRAY;
  const defaultUnifiedLiquidityMarketSymbols = useMemo(
    () =>
      getUnifiedLiquiditySelectionsForMarketAccounts({
        symbol: security?.Symbol,
        selectedMarketAccounts,
        marketAccountsByName,
        securitiesBySymbol,
        unifiedLiquidityTokenBySymbol,
      }),
    [marketAccountsByName, securitiesBySymbol, security?.Symbol, selectedMarketAccounts, unifiedLiquidityTokenBySymbol]
  );
  const selectedUnifiedLiquidityMarketSymbols = enableUnifiedLiquidity
    ? (unifiedLiquidityMarketSymbols ?? defaultUnifiedLiquidityMarketSymbols)
    : EMPTY_ARRAY;

  const handleNewMarketSelectorChange = useCallback(
    (value: MarketSelectorValue) => {
      const selections = value.selections;
      const nextMarkets = selections.filter(mktOrAccName => marketsByName.has(mktOrAccName));
      const nextMarketAccounts = selections.filter(mktOrAccName => marketAccountsByName.has(mktOrAccName));
      const aggregation = value.type === 'preset' ? value.presetID : undefined;
      onUpdateParameters({
        markets: nextMarkets,
        marketAccounts: nextMarketAccounts,
        aggregation,
      });
    },
    [onUpdateParameters, marketsByName, marketAccountsByName]
  );

  const marketSelectorItems = useMarketDataMarketSelectorItems({
    symbol,
    includeUnifiedLiquidityMarkets: enableUnifiedLiquidity,
  });

  const handleChangeUnifiedLiquidity = useCallback(
    (checked: boolean) => {
      mixpanel.track(MixpanelEvent.ToggleUnifiedLiquidity, {
        [MixpanelEventProperty.Enabled]: checked,
      });
      onUpdateParameters({
        enableUnifiedLiquidity: checked,
        unifiedLiquidityMarketSymbols: checked ? defaultUnifiedLiquidityMarketSymbols : [],
      });
    },
    [defaultUnifiedLiquidityMarketSymbols, mixpanel, onUpdateParameters]
  );

  const unsettledEnrichment = useUnsettledEnrichment({ security, marketSelectorItems });

  const buyingPowerEnrichment = useMarketSelectorBuyingPowerEnrichment({
    symbol: security?.Symbol ?? '',
    currency: security?.BaseCurrency ?? '',
    threshold: '10s',
    quantity: undefined,
    tag: 'PanelSettingsMarketSelector',
    side: undefined,
    simpleModeVisibleColIDs: EMPTY_ARRAY,
  });

  const enrichmentSpecs = useMemo(
    () => compact([unsettledEnrichment, buyingPowerEnrichment]),
    [unsettledEnrichment, buyingPowerEnrichment]
  );
  const { aggregationPresets, aggregationPresetsByID } = useMarketSelectorAggregationPresets();
  const itemsMap = useMemo(
    () => new Map(marketSelectorItems.map(option => [option.name, option])),
    [marketSelectorItems]
  );
  const marketSelectorValue: MarketSelectorValue = useMemo(() => {
    const selections = [...selectedMarkets, ...selectedMarketAccounts];
    if (aggregation) {
      return { type: 'preset', presetID: aggregation, selections };
    } else if (selectedMarkets.length || selectedMarketAccounts.length) {
      return { type: 'custom', selections };
    } else if (orderPreset?.aggregation) {
      const presetID = orderPreset.aggregation;
      const selections = getSelectableMarketItemNames(presetID, aggregationPresetsByID, itemsMap);
      return {
        type: 'preset',
        presetID,
        selections,
      };
    }
    return { type: 'custom', selections };
  }, [
    itemsMap,
    selectedMarketAccounts,
    selectedMarkets,
    aggregation,
    orderPreset?.aggregation,
    aggregationPresetsByID,
  ]);

  const unifiedLiquidityExtensionFactory = useUnifiedLiquidityMarketSelectorExtensionFactory({
    enabled: !!enableUnifiedLiquidity,
    onEnabledChange: handleChangeUnifiedLiquidity,
    showToggle: showUnifiedLiquidityOption && !!allowUnifiedLiquidity,
    items: marketSelectorItems,
    symbol: security?.Symbol,
    unifiedLiquidityCombinePerpsAndCFDs,
    selectionTarget: {
      type: 'external',
      selections: enableUnifiedLiquidity ? selectedUnifiedLiquidityMarketSymbols : EMPTY_ARRAY,
      onChange: unifiedLiquidityMarketSymbols => onUpdateParameters({ unifiedLiquidityMarketSymbols }),
    },
  });
  const extensions = useMemo(
    () => (unifiedLiquidityExtensionFactory ? [unifiedLiquidityExtensionFactory] : []),
    [unifiedLiquidityExtensionFactory]
  );

  const marketSelector = useMarketSelector({
    value: marketSelectorValue,
    onChange: handleNewMarketSelectorChange,
    presets: aggregationPresets,
    items: marketSelectorItems,
    enrichmentSpecs,
  });

  const unifiedLiquidityMarkets = useMemo(
    () => (marketAccounts ? getMultilegUnifiedLiquidityMarketAccounts(marketAccounts) : EMPTY_ARRAY),
    [marketAccounts]
  );
  const unifiedLiquidityTooltipGroups = useMemo(
    () =>
      security && unifiedLiquidityToken
        ? getSelectedUnifiedLiquidityMarketTooltipGroups({
            primarySymbol: security.Symbol,
            primarySecurity: security,
            unifiedLiquidityTokens: unifiedLiquidityToken.Tokens,
            selectedMarkets,
            selectedMarketAccounts,
            selectedUnifiedLiquidityMarketSymbols,
            marketAccountsByMarket,
            marketAccountsByName,
            marketDisplayNameByName,
            securitiesBySymbol,
          })
        : EMPTY_ARRAY,
    [
      selectedUnifiedLiquidityMarketSymbols,
      marketAccountsByMarket,
      marketAccountsByName,
      marketDisplayNameByName,
      securitiesBySymbol,
      security,
      selectedMarketAccounts,
      selectedMarkets,
      unifiedLiquidityToken,
    ]
  );

  const editMultilegDialog = useCreateMultilegDialog({
    onUseSymbol: onSymbolChange,
  });

  const handleOpenEditMultileg = useCallback(
    (symbol: string) => {
      if (!security) {
        return;
      }
      mixpanel.track(MixpanelEvent.EditMultileg);
      editMultilegDialog.onEdit(symbol);
    },
    [security, mixpanel, editMultilegDialog]
  );

  const handleUnifiedLiquidityMarketsChange = useCallback(
    (marketAccounts: string[]) => {
      onUpdateParameters({ marketAccounts: toMultilegSelectedMarketAccounts(marketAccounts) });
    },
    [onUpdateParameters]
  );

  const handleOrderPresetToggleFavorite = useCallback(
    (orderPreset: IOrderPreset) => {
      const updated = {
        ...orderPreset,
        isFavorite: !orderPreset.isFavorite,
      };
      mixpanel.track(MixpanelEvent.FavoriteOrderPreset);
      updateOrderPreset(orderPreset.id, updated);
    },
    [updateOrderPreset, mixpanel]
  );

  const handleOrderPresetChange = useCallback(
    (newOrderPreset: IOrderPreset | undefined) => {
      const orderPresetID = newOrderPreset?.id;
      const updatedParams: Partial<SingleSymbolPanelConfig> = {
        orderPresetID,
      };
      const newAggregation = newOrderPreset?.aggregation;
      if (newAggregation) {
        marketSelector.selectPreset(newAggregation);
      } else if (newOrderPreset == null && orderPreset != null && orderPreset.aggregation === aggregation) {
        marketSelector.unselectAll();
      }
      const allocations = newOrderPreset?.allocations;
      if (enableDefaultMarketCardSubAccount && allocations && allocations.length === 1 && allocations[0]?.subAccount) {
        updatedParams.subAccount = allocations[0]?.subAccount;
      } else if (
        enableDefaultMarketCardSubAccount &&
        newOrderPreset == null &&
        orderPreset != null &&
        orderPreset.allocations.length === 1 &&
        subAccount === orderPreset.allocations[0].subAccount
      ) {
        updatedParams.subAccount = undefined;
      }
      mixpanel.track(MixpanelEvent.SelectOrderPreset);
      onUpdateParameters(updatedParams);
    },
    [
      onUpdateParameters,
      marketSelector,
      orderPreset,
      aggregation,
      subAccount,
      mixpanel,
      enableDefaultMarketCardSubAccount,
    ]
  );

  const handleSubAccountChange = useCallback(
    (subAccount?: ISubaccount) => {
      mixpanel.track(MixpanelEvent.SelectSubAccount);
      onUpdateParameters({ subAccount: subAccount?.Name });
    },
    [onUpdateParameters, mixpanel]
  );

  return (
    <>
      <Stack p="spacing12" overflow="auto" scrollbarGutter="stable" pr="spacing4">
        <FormGroup label="Symbol">
          <SymbolSelector
            availableSecurities={filteredSecurities}
            appearance={SymbolSelectorAppearance.Basic}
            size={FormControlSizes.Small}
            showGroups={true}
            symbol={symbol}
            placeholder="Select a symbol"
            onSymbolChanged={onSymbolChange}
            dropdownWidth="480px"
            portalize={true}
            dropdownPlacement="bottom-end"
            showFavorites
            includeSedol={enableSEDOLDisplay}
          />
        </FormGroup>
        {showPresets && orderPresetsList.length > 0 ? (
          <FormGroup controlId="order-preset" label="Order Preset">
            <OrderPresetSearchSelect
              onChange={handleOrderPresetChange}
              data-testid="panel-settings-order-preset"
              prefix={<OrderPresetIcon />}
              selection={orderPreset}
              options={orderPresetOptions}
              size={FormControlSizes.Small}
              placeholder="Optional"
              id="order-form-preset-selector"
              onToggleFavorite={handleOrderPresetToggleFavorite}
            />
          </FormGroup>
        ) : null}
        {shoeMarketAccounts && enableDefaultMarketCardSubAccount && (
          <FormGroup label="SubAccount" size={FormControlSizes.Small}>
            <SubAccountSearchSelect
              selection={subAccountSelection}
              size={FormControlSizes.Small}
              options={subAccountOptions}
              onChange={handleSubAccountChange}
              data-testid="sub-account-selector"
              placeholder="Optional"
            />
          </FormGroup>
        )}
        {security && allowUnifiedLiquidity && showUnifiedLiquidityOption && unifiedLiquidityParameter && (
          <FormGroup
            size={FormControlSizes.Small}
            label={unifiedLiquidityParameter.DisplayName}
            tooltip={
              unifiedLiquidityTooltipGroups.length ? (
                <UnifiedLiquidityMarketTooltipContent
                  groups={unifiedLiquidityTooltipGroups}
                  dataTestId="single-symbol-settings-unified-liquidity-tooltip"
                  statusTestIdPrefix="single-symbol-settings-unified-liquidity-tooltip-status"
                />
              ) : undefined
            }
            tooltipProps={{ isSmall: false }}
            inline
            mb="spacing8"
            controlId="title"
            labelWidth="6rem"
            alignItems="center"
            justifyContent="space-between"
          >
            <Toggle
              size={FormControlSizes.Default}
              checked={enableUnifiedLiquidity ?? false}
              onChange={handleChangeUnifiedLiquidity}
              data-testid="panel-settings-unified-liquidity-toggle"
            />
          </FormGroup>
        )}
        {customSettings}
        <FormGroup label="Markets" display="flex" flexDirection="column" mb={0}>
          {isMultileg(security) ? (
            <MultiLegSummary
              security={security}
              readOnly={false}
              supportsAllMarkets={true}
              touched={true}
              unifiedLiquidityToken={enableUnifiedLiquidity ? unifiedLiquidityToken : undefined}
              unifiedLiquidityMarkets={unifiedLiquidityMarkets}
              onEditSymbol={handleOpenEditMultileg}
              onUnifiedLiquidityMarketsChange={handleUnifiedLiquidityMarketsChange}
              allocations={EMPTY_ARRAY}
            />
          ) : (
            <MarketSelector
              size={FormControlSizes.Small}
              marketSelectorAPI={marketSelector}
              showCredentialsOnlyToggle
              panelOffset={8}
              panelPlacement="auto"
              isNarrow={isPopout}
              placeholder={orderPreset?.aggregation ? orderPreset.aggregation : 'Select Aggregation...'}
              extensions={extensions}
            />
          )}
        </FormGroup>
      </Stack>
      <Grid
        mt="auto"
        gap="spacing8"
        p="spacing12"
        gridTemplateColumns="1fr"
        borderColor="backgroundDivider"
        borderTop="solid 1px"
      >
        <Button onClick={onClose} size={FormControlSizes.Small}>
          Close
        </Button>
      </Grid>
    </>
  );
}
